import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { Coupon, CouponRedemption, CreateCouponRequest, UpdateCouponRequest } from '@/types'
import { toast } from 'sonner'

const COUPONS_QUERY_KEY = 'coupons'

export const useCoupons = () => {
  return useQuery<Coupon[]>({
    queryKey: [COUPONS_QUERY_KEY],
    queryFn: async () => {
      const { data } = await api.get('/coupons')
      return data
    },
  })
}

export const useCoupon = (id: string) => {
  return useQuery<Coupon>({
    queryKey: [COUPONS_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/coupons/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useCreateCoupon = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (coupon: CreateCouponRequest) => {
      const { data } = await api.post('/coupons', coupon)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COUPONS_QUERY_KEY] })
      toast.success('Cupón creado exitosamente')
    },
  })
}

export const useUpdateCoupon = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateCouponRequest }) => {
      const { data } = await api.patch(`/coupons/${id}`, updates)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [COUPONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [COUPONS_QUERY_KEY, variables.id] })
      toast.success('Cupón actualizado')
    },
  })
}

export const useToggleCoupon = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/coupons/${id}/toggle`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COUPONS_QUERY_KEY] })
      toast.success('Estado del cupón actualizado')
    },
  })
}

export const useDeleteCoupon = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/coupons/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COUPONS_QUERY_KEY] })
      toast.success('Cupón eliminado')
    },
  })
}

export const useCouponRedemptions = (couponId: string) => {
  return useQuery<CouponRedemption[]>({
    queryKey: [COUPONS_QUERY_KEY, couponId, 'redemptions'],
    queryFn: async () => {
      const { data } = await api.get(`/coupons/${couponId}/redemptions`)
      return data
    },
    enabled: !!couponId,
  })
}
